import { TILE_SIZE } from '@etown/shared';
import { CAMERA_LERP, CAMERA_ZOOM } from '../lib/constants';
import type { AnchorHeight, IProjection, ScreenPoint } from './IProjection';

/**
 * Kamera der Draufsicht: Tile-Koordinaten (Mittelpunkt) mit weichem Folgen.
 * Zugleich die orthogonale Projektion — in dieser Ansicht ist jeder Punkt der
 * Ebene darstellbar, `worldToScreen` liefert deshalb nie null.
 */
export class Camera implements IProjection {
  x = 0;
  y = 0;
  zoom = CAMERA_ZOOM;
  viewportWidth = 1;
  viewportHeight = 1;

  private following = false;

  jumpTo(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.following = true;
  }

  follow(targetX: number, targetY: number): void {
    // Beim ersten Ziel springen, sonst gleitet die Kamera vom Ursprung heran
    if (!this.following) {
      this.jumpTo(targetX, targetY);
      return;
    }
    this.x += (targetX - this.x) * CAMERA_LERP;
    this.y += (targetY - this.y) * CAMERA_LERP;
  }

  setViewport(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
  }

  /**
   * Von oben betrachtet gibt es keine Tiefe: Overlays werden schlicht um
   * ihren Bildschirm-Versatz nach oben geschoben und behalten ihre Groesse.
   */
  worldToScreen(wx: number, wy: number, height?: AnchorHeight): ScreenPoint {
    const scale = TILE_SIZE * this.zoom;
    return {
      x: (wx - this.x) * scale + this.viewportWidth / 2,
      y: (wy - this.y) * scale + this.viewportHeight / 2 + (height?.screenOffsetY ?? 0),
      scale: 1,
    };
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const scale = TILE_SIZE * this.zoom;
    return {
      x: (sx - this.viewportWidth / 2) / scale + this.x,
      y: (sy - this.viewportHeight / 2) / scale + this.y,
    };
  }
}
